import React from "react";
import DoorRenderer from "./DoorRenderer";
import WindowRenderer from "./WindowRenderer";
import PanoramicWindowRenderer from "./PanoramicWindowRenderer";
import WindowBlindsRenderer from "./WindowBlindsRenderer";
import OutletRenderer from "./OutletRenderer";
import Outlet2Renderer from "./Outlet2Renderer";
import RadiatorRenderer from "./RadiatorRenderer";
import KitchenElementRenderer from "./KitchenElementRenderer";
import SwitchRenderer from "./SwitchRenderer";
import SwitchDoubleRenderer from "./SwitchDoubleRenderer";
import HeaterRenderer from "./HeaterRenderer";
import ToiletRenderer from "./ToiletRenderer";
import WaterHeaterRenderer from "./WaterHeaterRenderer";
import HandBasinRenderer from "./HandBasinRenderer";
import AirConditionerInternalRenderer from "./AirConditionerInternalRenderer";
import AirConditionerExternalRenderer from "./AirConditionerExternalRenderer";
import ShowerRenderer from "./ShowerRenderer";
import CustomElementRenderer from "./CustomElementRenderer";
import { ReactComponent as WallSvg } from "../../Assets/wall.svg";
import {
  OBJECT_TYPES,
  GRID_CELL_SIZE_M,
  SELECTED_ELEMENT_COLOR,
  PLACEMENT_HIGHLIGHT_FILL_COLOR,
  PLACEMENT_NOT_ALLOWED_FILL_COLOR,
  defaultObjectSizes,
  WALL_THICKNESS_M_RENDER,
  CORRIDOR_WALL_THICKNESS_M,
} from "../configuratorConstants";

const WallSegmentRenderer = ({
  segmentData,
  moduleObj,
  cellX,
  cellY,
  orientation,
  scale,
  cellSize = GRID_CELL_SIZE_M,
  isSelected,
  selectedElementId,
  onSelectWallSegment,
  onSelectElement,
  onContextMenu,
  highlightForPlacement,
  isGhost = false,
  isInPlacementMode,
  currentFloorIndex,
  elementTypeToPlace,
}) => {
  const { id: segmentId, elements, isDocked, thickness } = segmentData;

  if (isDocked) {
    return null;
  }

  const currentElements = Array.isArray(elements) ? elements : [];

  const isTopWall = orientation === "h" && cellY === 0;
  const isBottomWall = orientation === "h" && cellY === moduleObj.cellsLong;
  const isLeftWall = orientation === "v" && cellX === 0;
  const isRightWall = orientation === "v" && cellX === moduleObj.cellsWide;

  const isExternalWall = isTopWall || isBottomWall || isLeftWall || isRightWall;
  const isInternalWall = !isExternalWall && !isDocked;
  const defaultThickness = isInternalWall ? CORRIDOR_WALL_THICKNESS_M : WALL_THICKNESS_M_RENDER;
  const visualThicknessPx = (thickness || defaultThickness) * scale;

  let determinedPlacementSideFactor;
  let wallOrientationType = "internal";

  if (isTopWall) {
    determinedPlacementSideFactor = 1;
    wallOrientationType = "top";
  } else if (isBottomWall) {
    determinedPlacementSideFactor = -1;
    wallOrientationType = "bottom";
  } else if (isLeftWall) {
    determinedPlacementSideFactor = -1;
    wallOrientationType = "left";
  } else if (isRightWall) {
    determinedPlacementSideFactor = 1;
    wallOrientationType = "right";
  } else {
    determinedPlacementSideFactor = 1;
    wallOrientationType = "internal";
  }

  const windowShouldFlip = wallOrientationType === "bottom" || wallOrientationType === "left";

  let baseTransform;
  const cellSizePx = cellSize * scale;
  const moduleWidthPx = moduleObj.width * scale;
  const moduleHeightPx = moduleObj.height * scale;

  if (orientation === "h") {
    let yPos = cellY * cellSizePx;
    if (isTopWall) {
      yPos = visualThicknessPx / 2;
    } else if (isBottomWall) {
      yPos = moduleHeightPx - visualThicknessPx / 2;
    }
    baseTransform = `translate(${cellX * cellSizePx}, ${yPos})`;
  } else {
    let xPos = cellX * cellSizePx;
    if (isLeftWall) {
      xPos = visualThicknessPx / 2;
    } else if (isRightWall) {
      xPos = moduleWidthPx - visualThicknessPx / 2;
    }
    baseTransform = `translate(${xPos}, ${cellY * cellSizePx}) rotate(90)`;
  }

  const handleWallContextMenu = (e) => {
    if (isGhost) return;
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) {
      onContextMenu(e, segmentId, OBJECT_TYPES.WALL_SEGMENT, {
        isExternal: isExternalWall,
        isInternal: !isExternalWall && !isDocked,
        currentThickness: thickness,
        moduleId: moduleObj.id,
        segmentKeyInModule: `${cellX},${cellY}_${orientation}`,
      });
    }
  };

  const renderWallBody = () => {
    let overlayFill = "none";
    if (isSelected) {
      overlayFill = "rgba(0, 123, 255, 0.3)";
    } else if (isInPlacementMode && !isGhost && elementTypeToPlace) {
      overlayFill = highlightForPlacement
        ? PLACEMENT_HIGHLIGHT_FILL_COLOR
        : PLACEMENT_NOT_ALLOWED_FILL_COLOR;
    }

    const wallProps = {
      y: -visualThicknessPx / 2,
      height: visualThicknessPx,
    };

    let wallRenderX = 0;
    let wallRenderWidth = cellSizePx;
    const offsetV = visualThicknessPx * 1.05;
    const offsetH = visualThicknessPx * 0.3;

    if (orientation === 'v' && isExternalWall && !isDocked) {
      const isTopNeighborDocked = Object.keys(moduleObj.wallSegments || {}).some(key => {
        const [coords, orient] = key.split('_');
        const y = parseInt(coords.split(',')[1]);
        return orient === 'h' && y === 0 && moduleObj.wallSegments[key].isDocked;
      });
      const isBottomNeighborDocked = Object.keys(moduleObj.wallSegments || {}).some(key => {
        const [coords, orient] = key.split('_');
        const y = parseInt(coords.split(',')[1]);
        return orient === 'h' && y === moduleObj.cellsLong && moduleObj.wallSegments[key].isDocked;
      });
      if (cellY === 0 && !isTopNeighborDocked) {
        wallRenderX = offsetV;
        wallRenderWidth -= offsetV;
      }
      if (cellY === moduleObj.cellsLong - 1 && !isBottomNeighborDocked) {
        wallRenderWidth -= offsetV;
      }
    }

    if (orientation === 'h' && isExternalWall && !isDocked) {
      if (cellX === 0) {
        wallRenderX = offsetH;
        wallRenderWidth -= offsetH;
      }
      if (cellX === moduleObj.cellsWide - 1) {
        wallRenderWidth -= offsetH;
      }
    }

    const openings = currentElements
      .filter((el) => el.type === OBJECT_TYPES.DOOR || el.type === OBJECT_TYPES.PANORAMIC_WINDOW)
      .sort((a, b) => (a.positionOnSegment || 0.5) - (b.positionOnSegment || 0.5));

    if (openings.length === 0) {
      return (
        <g
          data-object-id={segmentId}
          data-object-type={OBJECT_TYPES.WALL_SEGMENT}
          data-module-id={moduleObj.id}
          data-wall-cell-x={cellX}
          data-wall-cell-y={cellY}
          data-wall-orientation={orientation}
          className={isGhost ? "cursor-default" : "cursor-pointer"}
          onClick={(e) => {
            if (isGhost) return;
            console.log('🖱️ [WallSegmentRenderer] Wall clicked (no openings):', {
              segmentId,
              isInPlacementMode,
              elementTypeToPlace,
              hasOnSelectWallSegment: !!onSelectWallSegment
            });
            e.stopPropagation();
            if (onSelectWallSegment) {
              onSelectWallSegment(segmentId);
            }
          }}
        >
          <WallSvg
            {...wallProps}
            x={wallRenderX}
            width={wallRenderWidth}
            preserveAspectRatio="none"
          />
          {overlayFill !== "none" && (
            <rect
              {...wallProps}
              x={wallRenderX}
              width={wallRenderWidth}
              fill={overlayFill}
              stroke={isSelected ? SELECTED_ELEMENT_COLOR : "none"}
              strokeWidth={1}
              pointerEvents="none"
            />
          )}
        </g>
      );
    }

    let segmentsToRender = [{ start: wallRenderX, end: wallRenderX + wallRenderWidth }];
    openings.forEach((opening) => {
      let openingWidthM;
      if (opening.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
        // Панорамное окно занимает 2 сэндвича
        openingWidthM = GRID_CELL_SIZE_M * 2;
        console.log('🪟 [PANORAMIC WINDOW] WallSegment opening calculation:', {
          openingWidthM,
          GRID_CELL_SIZE_M,
          calculated: GRID_CELL_SIZE_M * 2,
          elementWidth: opening.width,
          defaultWidth: defaultObjectSizes[opening.type]?.width,
          positionOnSegment: opening.positionOnSegment,
          cellSizePx,
          wallRenderWidth
        });
      } else {
        openingWidthM = opening.width || defaultObjectSizes[opening.type]?.width || 0;
      }
      const openingWidthPx = openingWidthM * scale;
      const openingCenterPx = opening.positionOnSegment * cellSizePx;
      const openingStartPx = openingCenterPx - openingWidthPx / 2;
      const openingEndPx = openingStartPx + openingWidthPx;

      if (opening.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
        console.log('✂️ [PANORAMIC WINDOW] WallSegment opening creation:', {
          openingWidthPx,
          openingCenterPx,
          openingStartPx,
          openingEndPx,
          openingSpan: openingEndPx - openingStartPx,
          wallRenderX,
          wallRenderWidth,
          segmentsBefore: segmentsToRender.length
        });
      }
      const newSegments = [];
      segmentsToRender.forEach((seg) => {
        // For panoramic windows: if the opening overlaps with the segment at all, remove the entire segment
        if (opening.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
          const hasOverlap = openingStartPx < seg.end && openingEndPx > seg.start;
          if (hasOverlap) {
            // Completely remove this wall segment
            console.log('🗑️ [PANORAMIC WINDOW] Removing entire wall segment:', {
              segStart: seg.start,
              segEnd: seg.end,
              segWidth: seg.end - seg.start,
              openingStart: openingStartPx,
              openingEnd: openingEndPx,
              overlap: true
            });
            // Don't add the segment - it's completely removed
          } else {
            // No overlap - keep the segment
            newSegments.push(seg);
          }
        } else {
          // For regular openings (doors, etc.) - old logic: split the segment
          if (openingStartPx < seg.end && openingEndPx > seg.start) {
            if (openingStartPx > seg.start) {
              newSegments.push({ start: seg.start, end: openingStartPx });
            }
            if (openingEndPx < seg.end) {
              newSegments.push({ start: openingEndPx, end: seg.end });
            }
          } else {
            newSegments.push(seg);
          }
        }
      });
      segmentsToRender = newSegments;
    });
    return (
      <g
        data-object-id={segmentId}
        data-object-type={OBJECT_TYPES.WALL_SEGMENT}
        data-module-id={moduleObj.id}
        data-wall-cell-x={cellX}
        data-wall-cell-y={cellY}
        data-wall-orientation={orientation}
        className={isGhost ? "cursor-default" : "cursor-pointer"}
        onClick={(e) => {
          if (isGhost) return;
          console.log('🖱️ [WallSegmentRenderer] Wall clicked (with openings):', {
            segmentId,
            isInPlacementMode,
            elementTypeToPlace,
            hasOnSelectWallSegment: !!onSelectWallSegment
          });
          e.stopPropagation();
          if (onSelectWallSegment) {
            onSelectWallSegment(segmentId);
          }
        }}
      >
        {segmentsToRender.map((seg, index) => (
          <WallSvg
            key={index}
            {...wallProps}
            x={seg.start}
            width={seg.end - seg.start}
            preserveAspectRatio="none"
          />
        ))}
        {overlayFill !== "none" && (
          <rect
            x={wallRenderX}
            y={wallProps.y}
            width={wallRenderWidth}
            height={wallProps.height}
            fill={overlayFill}
            stroke={isSelected ? SELECTED_ELEMENT_COLOR : "none"}
            strokeWidth={1}
            pointerEvents="none"
          />
        )}
      </g>
    );
  };

  const renderElements = () => {
    if (!currentElements || currentElements.length === 0) return null;

    const getElementRenderOrder = (type) => {
      switch (type) {
        case OBJECT_TYPES.WINDOW:
        case OBJECT_TYPES.PANORAMIC_WINDOW:
          return 1;
        case OBJECT_TYPES.WINDOW_BLINDS:
          return 2;
        default:
          return 3;
      }
    };

    const sortedElements = [...currentElements].sort((a, b) => {
      return getElementRenderOrder(a.type) - getElementRenderOrder(b.type);
    });

    const smallElements = sortedElements.filter(
      (el) =>
        el.type !== OBJECT_TYPES.DOOR &&
        el.type !== OBJECT_TYPES.WINDOW &&
        el.type !== OBJECT_TYPES.PANORAMIC_WINDOW,
    );
    const numSmallElements = smallElements.length;
    let smallElementIndex = 0;

    return (
      <g>
        {sortedElements.map((element) => {
          if (!element || !element.type) return null;

          // Don't render adjacent copies of panoramic windows, but allow other elements (like window blinds)
          if (element.isAdjacentCopy && element.type === OBJECT_TYPES.PANORAMIC_WINDOW) return null;

          let currentPositionOnSegment = element.positionOnSegment || 0.5;
          const isLargeOpeningElement =
            element.type === OBJECT_TYPES.DOOR ||
            element.type === OBJECT_TYPES.WINDOW ||
            element.type === OBJECT_TYPES.PANORAMIC_WINDOW;



          const elementPlacementOffsetXPx = cellSizePx * currentPositionOnSegment;
          const individualElementTransform = `translate(${elementPlacementOffsetXPx}, 0)`;
          const handleElementContextMenu = (eInner) => {
            if (isGhost) return;
            eInner.preventDefault();
            eInner.stopPropagation();
            if (onContextMenu)
              onContextMenu(eInner, element.id, element.type, {});
          };

          const finalPlacementSideFactor = determinedPlacementSideFactor * (element.placementSide || 1);
          const shouldHighlightWindowForBlinds = elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS &&
            (element.type === OBJECT_TYPES.WINDOW || element.type === OBJECT_TYPES.PANORAMIC_WINDOW) &&
            !currentElements.some(el =>
              el.type === OBJECT_TYPES.WINDOW_BLINDS &&
              Math.abs(el.positionOnSegment - element.positionOnSegment) < 0.1
            );

          const associatedWindow = element.type === OBJECT_TYPES.WINDOW_BLINDS
            ? currentElements.find(
              w => {
                const isWindow = (w.type === OBJECT_TYPES.WINDOW || w.type === OBJECT_TYPES.PANORAMIC_WINDOW);
                if (!isWindow) return false;
                const posDiff = Math.abs((w.positionOnSegment || 0.5) - (element.positionOnSegment || 0.5));
                const isCloseEnough = posDiff < 0.01;
                if (element.type === OBJECT_TYPES.WINDOW_BLINDS && !isCloseEnough && posDiff < 0.2) {
                  console.log('⚠️ [WallSegmentRenderer] Blind mismatch:', {
                    blindId: element.id,
                    windowId: w.id,
                    blindPos: element.positionOnSegment,
                    windowPos: w.positionOnSegment,
                    diff: posDiff
                  });
                }
                return isCloseEnough;
              }
            )
            : null;

          if (element.type === OBJECT_TYPES.WINDOW_BLINDS && !associatedWindow) {
            console.log('❌ [WallSegmentRenderer] Blind has NO associated window:', {
              blindId: element.id,
              pos: element.positionOnSegment,
              availableWindows: currentElements.filter(e => e.type === OBJECT_TYPES.WINDOW).map(e => ({ id: e.id, pos: e.positionOnSegment }))
            });
          }

          const commonAccessoryProps = {
            element: { ...element, width: element.width || GRID_CELL_SIZE_M },
            scale,
            isSelected: selectedElementId === element.id,
            wallThickness: visualThicknessPx / scale,
            placementSideFactor: finalPlacementSideFactor,
            wallOrientationType: wallOrientationType,
            orientation: orientation,
            highlightForBlindsPlacement: shouldHighlightWindowForBlinds,
            associatedWindow: associatedWindow,
          };

          const renderers = {
            [OBJECT_TYPES.DOOR]: <DoorRenderer {...commonAccessoryProps} />,
            [OBJECT_TYPES.WINDOW]: (<WindowRenderer {...commonAccessoryProps} windowVisualNeedsFlip={windowShouldFlip} />),
            [OBJECT_TYPES.PANORAMIC_WINDOW]: (<PanoramicWindowRenderer {...commonAccessoryProps} windowVisualNeedsFlip={windowShouldFlip} />),
            [OBJECT_TYPES.WINDOW_BLINDS]: (<WindowBlindsRenderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.OUTLET]: <OutletRenderer {...commonAccessoryProps} />,
            [OBJECT_TYPES.OUTLET_DOUBLE]: (<Outlet2Renderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.SWITCH]: <SwitchRenderer {...commonAccessoryProps} />,
            [OBJECT_TYPES.SWITCH_DOUBLE]: (<SwitchDoubleRenderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.RADIATOR]: (<RadiatorRenderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.KITCHEN_ELEMENT]: (<KitchenElementRenderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.HEATER]: <HeaterRenderer {...commonAccessoryProps} />,
            [OBJECT_TYPES.TOILET]: <ToiletRenderer {...commonAccessoryProps} />,
            [OBJECT_TYPES.WATER_HEATER]: (<WaterHeaterRenderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.HAND_BASIN]: (<HandBasinRenderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.INTERNAL_AIR_CONDITIONER]: (<AirConditionerInternalRenderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER]: (<AirConditionerExternalRenderer {...commonAccessoryProps} />),
            [OBJECT_TYPES.SHOWER]: <ShowerRenderer {...commonAccessoryProps} />,
          };

          // Если рендерер не найден в стандартных, используем CustomElementRenderer для элементов из БД
          const elementRenderer = renderers[element.type] || <CustomElementRenderer {...commonAccessoryProps} />;

          return (
            <g
              key={element.id}
              transform={individualElementTransform}
              onContextMenu={handleElementContextMenu}
              onClick={(eInner) => {
                if (isGhost) return;
                eInner.stopPropagation();
                onSelectElement(element.id);
              }}
              className={isGhost ? "cursor-default" : "cursor-pointer"}
              data-object-id={element.id}
              data-object-type={element.type}
            >
              {elementRenderer}
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <g
      transform={baseTransform}
      onContextMenu={handleWallContextMenu}
      data-segment-id={segmentId}
      data-is-external-wall={isExternalWall.toString()}
      data-current-floor-index={currentFloorIndex}
    >
      {renderWallBody()}
      {renderElements()}
    </g>
  );
};

export default React.memo(WallSegmentRenderer);